import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { PageHeader, Section, CTABanner } from "@/components/blocks";

const FAMILY_STEPS = [
  { t: "Share your request", d: "Tell us about the care you need — arrangement, location, schedule and your children's ages." },
  { t: "We review personally", d: "The team reviews your request and considers which nannies may genuinely suit your household." },
  { t: "We reach out", d: "We contact you using your preferred method to talk through next steps. Nothing is booked or confirmed automatically." },
  { t: "Introductions", d: "When there's a good potential fit, we introduce nannies with the context you need to decide." },
];

const NANNY_STEPS = [
  { t: "Apply", d: "Complete the application with your experience, the age groups you've cared for and your availability." },
  { t: "Private review", d: "Your details and any documents you share stay private to our team — never published." },
  { t: "We get in touch", d: "If we can represent you, we'll reach out to learn more and understand what you're looking for." },
  { t: "Considered matching", d: "We introduce you to families where we believe there's a genuine fit." },
];

const Column = ({ title, steps, testid }) => (
  <div data-testid={testid}>
    <h2 className="font-serif text-2xl tracking-tight">{title}</h2>
    <ol className="mt-8 space-y-8">
      {steps.map((s, i) => (
        <li key={s.t} className="flex gap-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C6A04A]/40 font-serif text-lg text-[#C6A04A]">
            {i + 1}
          </span>
          <div className="pt-1">
            <h3 className="font-medium text-[#0B0A08]">{s.t}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-[#0B0A08]/70">{s.d}</p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

export default function HowItWorks() {
  useSeo({
    title: "How It Works — Nanny Poppins UAE",
    description:
      "A calm, step-by-step process for families requesting a nanny match and for nannies applying to be represented.",
  });

  return (
    <div>
      <PageHeader
        eyebrow="How It Works"
        title="A simple, honest process."
        intro="No accounts, no public listings and no automatic bookings — just a considered process handled by our team."
      />

      <Section>
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Column title="For families" steps={FAMILY_STEPS} testid="how-family" />
          <Column title="For nannies" steps={NANNY_STEPS} testid="how-nanny" />
        </div>

        <div className="mt-14 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/request-a-nanny-match"
            data-testid="how-family-cta"
            className="inline-flex items-center justify-center rounded-full bg-[#0B0A08] px-7 py-3.5 text-sm font-semibold text-[#F7F4ED] hover:bg-[#0B0A08]/85"
          >
            Request a Nanny Match
          </Link>
          <Link
            to="/for-nannies"
            data-testid="how-nanny-cta"
            className="inline-flex items-center justify-center rounded-full border border-[#0B0A08]/20 px-7 py-3.5 text-sm font-semibold text-[#0B0A08] hover:border-[#C6A04A] hover:text-[#C6A04A]"
          >
            Apply as a Nanny
          </Link>
        </div>
      </Section>

      <CTABanner />
    </div>
  );
}
